import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  EyeOff, 
  Star,
  Loader2,
  Check,
  Pencil,
  Globe,
  DollarSign,
  Tag,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMembershipPlans = () => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [countryConfig, setCountryConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Available countries
  const availableCountries = [
    { code: 'SL', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'EU', name: 'European Union', flag: '🇪🇺' }
  ];

  useEffect(() => {
    fetchCountryConfig(selectedCountry);
  }, [selectedCountry]);

  const fetchCountryConfig = async (countryCode) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/membership/country/${countryCode}`);
      if (response.data.success) {
        setCountryConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error fetching country config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (tierId) => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/membership/country/${selectedCountry}/tier/${tierId}/toggle-visibility`
      );
      if (response.data.success) {
        fetchCountryConfig(selectedCountry);
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSetRecommended = async (tierId) => {
    try {
      setSaving(true);
      const response = await axios.post(
        `${API_URL}/api/admin/membership/country/${selectedCountry}/tier/${tierId}/toggle-recommended`
      );
      if (response.data.success) {
        fetchCountryConfig(selectedCountry);
      }
    } catch (error) {
      console.error('Error setting recommended:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier({ ...tier });
    setEditDialogOpen(true);
  };

  const handleSaveTier = async () => {
    if (!editingTier) return;
    
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/admin/membership/country/${selectedCountry}/tier/${editingTier.tier_id}`,
        editingTier
      );
      if (response.data.success) {
        setEditDialogOpen(false);
        setEditingTier(null);
        fetchCountryConfig(selectedCountry);
      }
    } catch (error) {
      console.error('Error saving tier:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePromoSettings = async (enabled) => {
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/api/admin/membership/country/${selectedCountry}/settings`,
        null,
        { params: { promo_enabled: enabled } }
      );
      fetchCountryConfig(selectedCountry);
    } catch (error) {
      console.error('Error updating promo settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price, symbol) => {
    if (price === null || price === undefined) return '-';
    if (price === 0) return 'FREE';
    return `${symbol}${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Membership Plans</h1>
                <p className="text-sm text-gray-500">Manage pricing per country</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Country Selector Tabs */}
        <Tabs value={selectedCountry} onValueChange={setSelectedCountry} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            {availableCountries.map(country => (
              <TabsTrigger key={country.code} value={country.code} className="gap-2">
                <span>{country.flag}</span>
                <span>{country.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {countryConfig && (
          <>
            {/* Country Settings Card */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Country Settings
                </CardTitle>
                <CardDescription>
                  Currency and promotional settings for {availableCountries.find(c => c.code === selectedCountry)?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm text-gray-500">Currency</Label>
                    <p className="text-lg font-semibold">
                      {countryConfig.currency_symbol} {countryConfig.currency_code}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Promotional Pricing</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Switch
                        checked={countryConfig.promo_enabled}
                        onCheckedChange={handleUpdatePromoSettings}
                        disabled={saving}
                      />
                      <span className={countryConfig.promo_enabled ? 'text-green-600' : 'text-gray-500'}>
                        {countryConfig.promo_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-500">Active Tiers</Label>
                    <p className="text-lg font-semibold">
                      {countryConfig.tiers?.filter(t => t.is_visible).length} of {countryConfig.tiers?.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Membership Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {countryConfig.tiers?.sort((a, b) => a.display_order - b.display_order).map(tier => (
                <Card 
                  key={tier.tier_id}
                  className={`relative ${!tier.is_visible ? 'opacity-60 border-dashed' : ''} ${tier.is_recommended ? 'ring-2 ring-blue-500' : ''}`}
                >
                  {tier.is_recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-blue-600">
                        <Star className="h-3 w-3 mr-1" />
                        Recommended
                      </Badge>
                    </div>
                  )}
                  
                  {!tier.is_visible && (
                    <div className="absolute -top-3 right-4">
                      <Badge variant="secondary">
                        <EyeOff className="h-3 w-3 mr-1" />
                        Hidden
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="pt-6">
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    <CardDescription>{tier.description}</CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Pricing Section */}
                    <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Regular Price</span>
                        <span className="text-xl font-bold">
                          {formatPrice(tier.regular_price, countryConfig.currency_symbol)}
                          <span className="text-sm font-normal text-gray-500">/{tier.billing_cycle}</span>
                        </span>
                      </div>
                      
                      {countryConfig.promo_enabled && tier.promo_price !== null && tier.promo_days > 0 && (
                        <div className="flex justify-between items-center pt-2 border-t">
                          <div>
                            <span className="text-sm text-green-600 font-medium">{tier.promo_label}</span>
                          </div>
                          <span className="text-xl font-bold text-green-600">
                            {formatPrice(tier.promo_price, countryConfig.currency_symbol)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div>
                      <Label className="text-sm text-gray-500 mb-2 block">Features</Label>
                      <ul className="space-y-1">
                        {tier.features?.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {tier.features?.length > 4 && (
                          <li className="text-sm text-gray-500">
                            +{tier.features.length - 4} more features
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* AI Limits */}
                    <div className="flex gap-4 pt-2">
                      <div className="text-center">
                        <p className="text-lg font-bold text-purple-600">
                          {tier.ai_ads_limit === -1 ? '∞' : tier.ai_ads_limit}
                        </p>
                        <p className="text-xs text-gray-500">AI Ads</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-600">{tier.tone_count}</p>
                        <p className="text-xs text-gray-500">Tones</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEditTier(tier)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleVisibility(tier.tier_id)}
                        disabled={saving}
                      >
                        {tier.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant={tier.is_recommended ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleSetRecommended(tier.tier_id)}
                        disabled={saving}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Edit Tier Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingTier?.name} Plan</DialogTitle>
            <DialogDescription>
              Update pricing and features for {availableCountries.find(c => c.code === selectedCountry)?.name}
            </DialogDescription>
          </DialogHeader>

          {editingTier && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plan Name</Label>
                  <Input
                    value={editingTier.name}
                    onChange={(e) => setEditingTier({...editingTier, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Billing Cycle</Label>
                  <Input
                    value={editingTier.billing_cycle}
                    onChange={(e) => setEditingTier({...editingTier, billing_cycle: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={editingTier.description || ''}
                  onChange={(e) => setEditingTier({...editingTier, description: e.target.value})}
                />
              </div>

              {/* Pricing */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Pricing
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Regular Price ({countryConfig?.currency_symbol})</Label>
                    <Input
                      type="number"
                      value={editingTier.regular_price}
                      onChange={(e) => setEditingTier({...editingTier, regular_price: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Promo Price ({countryConfig?.currency_symbol})</Label>
                    <Input
                      type="number"
                      value={editingTier.promo_price || ''}
                      placeholder="Leave empty for no promo"
                      onChange={(e) => setEditingTier({...editingTier, promo_price: e.target.value ? parseFloat(e.target.value) : null})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Promo Days</Label>
                    <Input
                      type="number"
                      value={editingTier.promo_days || 0}
                      onChange={(e) => setEditingTier({...editingTier, promo_days: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Promo Label</Label>
                    <Input
                      value={editingTier.promo_label || ''}
                      placeholder="e.g., First 7 days FREE"
                      onChange={(e) => setEditingTier({...editingTier, promo_label: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* AI Limits */}
              <div className="p-4 bg-purple-50 rounded-lg space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  AI Features
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>AI Ads Limit (-1 for unlimited)</Label>
                    <Input
                      type="number"
                      value={editingTier.ai_ads_limit}
                      onChange={(e) => setEditingTier({...editingTier, ai_ads_limit: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Tone Count</Label>
                    <Input
                      type="number"
                      value={editingTier.tone_count}
                      onChange={(e) => setEditingTier({...editingTier, tone_count: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <Label>Features (one per line)</Label>
                <Textarea
                  rows={6}
                  value={editingTier.features?.join('\n') || ''}
                  onChange={(e) => setEditingTier({
                    ...editingTier, 
                    features: e.target.value.split('\n').filter(f => f.trim())
                  })}
                  placeholder="Enter features, one per line"
                />
              </div>

              {/* Display Order */}
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={editingTier.display_order}
                  onChange={(e) => setEditingTier({...editingTier, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTier} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembershipPlans;
